import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEmojis } from '../context/EmojiContext';

/**
 * Persistent 👩‍💻 emoji-inserter button anchored to the bottom-right of every
 * textarea in the app (and long single-line inputs, if they're wide enough).
 * Clicking any button opens the shared emoji keyboard and inserts the picked
 * emoji at the field's current cursor position, dispatching a native input
 * event so controlled React components stay in sync.
 *
 * Mounted once at the root of the app in App.js.
 */

const isEligibleField = (el) => {
  if (!el) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.closest('[data-emoji-ui="true"]')) return false;
  const tag = el.tagName;
  if (tag !== 'TEXTAREA' && tag !== 'INPUT') return false;
  if (tag === 'INPUT') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    if (!['text', 'search', 'email', ''].includes(type)) return false;
    if (type === 'search') return false;
  }
  // Skip search / find / filter fields — the emoji shortcut only makes sense
  // on inputs where you're actually writing copy.
  const hints = `${el.placeholder || ''} ${el.getAttribute('aria-label') || ''} ${el.name || ''}`.toLowerCase();
  if (/(search|filter|find\b|find a|find an|find the)/.test(hints)) return false;
  if (el.closest('[data-emoji-skip="true"]')) return false;
  return true;
};

const insertAtCursor = (el, emoji) => {
  if (!el) return;
  el.focus({ preventScroll: true });
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? el.value.length;
  const proto = el.tagName === 'TEXTAREA'
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  const nextValue = el.value.slice(0, start) + emoji + el.value.slice(end);
  if (setter) setter.call(el, nextValue); else el.value = nextValue;
  const caret = start + emoji.length;
  try { el.setSelectionRange(caret, caret); } catch { /* some input types don't support selection */ }
  el.dispatchEvent(new Event('input', { bubbles: true }));
};

const AnchoredButton = ({ el, onOpen }) => {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    let raf;
    const update = () => {
      if (!document.body.contains(el)) { setRect(null); return; }
      const r = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      const visible = style.visibility !== 'hidden' && style.display !== 'none' && r.width > 44 && r.height > 20;
      setRect(visible ? r : null);
    };
    update();
    const loop = () => { update(); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [el]);

  if (!rect) return null;
  const btnSize = 22;
  const top = rect.bottom - btnSize - 4;
  const left = rect.right - btnSize - 4;
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(el, e.currentTarget); }}
      style={{ position: 'fixed', top, left, width: btnSize, height: btnSize, pointerEvents: 'auto', zIndex: 9997 }}
      className="rounded-full bg-white/95 backdrop-blur border border-pink-200 shadow-sm hover:bg-pink-50 hover:border-pink-400 flex items-center justify-center text-[12px] leading-none transition"
      title="insert emoji"
      aria-label="insert emoji"
      data-testid="global-emoji-insert-btn"
    >
      <span style={{ transform: 'translateY(-0.5px)' }}>👩‍💻</span>
    </button>
  );
};

const GlobalEmojiInserter = () => {
  const { emojis, saveEmoji } = useEmojis();
  const [elements, setElements] = useState([]);
  const [activeTarget, setActiveTarget] = useState(null);
  const [pickerAnchor, setPickerAnchor] = useState(null);
  const [custom, setCustom] = useState('');

  // Rescan the DOM periodically + on mutations for eligible textareas.
  useEffect(() => {
    let last = new Set();
    const scan = () => {
      const found = [
        ...Array.from(document.querySelectorAll('textarea')),
        ...Array.from(document.querySelectorAll('input')),
      ].filter(isEligibleField);
      const same = found.length === last.size && found.every((el) => last.has(el));
      if (!same) {
        last = new Set(found);
        setElements(found);
      }
    };
    scan();
    const mo = new MutationObserver(scan);
    mo.observe(document.body, { childList: true, subtree: true });
    const iv = setInterval(scan, 700);
    return () => { mo.disconnect(); clearInterval(iv); };
  }, []);

  const openPicker = (targetEl, buttonEl) => {
    const r = buttonEl.getBoundingClientRect();
    const width = 288; // matches w-72
    const height = 300; // approx picker height — open above the button
    const dialogEl = targetEl.closest?.('[role="dialog"]');
    if (dialogEl) {
      // The dialog is a transformed, scrollable container. Position the picker ABSOLUTELY inside
      // it (in its scroll-content space) so it sits right next to the field and tracks scrolling.
      const dr = dialogEl.getBoundingClientRect();
      const contentTop = (r.top - dr.top) + dialogEl.scrollTop;
      const contentRight = (r.right - dr.left) + dialogEl.scrollLeft;
      const top = Math.max(8, contentTop - height);
      const left = Math.max(8, Math.min((dialogEl.scrollWidth || dialogEl.clientWidth) - width - 8, contentRight - width));
      setActiveTarget(targetEl);
      setPickerAnchor({ top, left, mode: 'absolute' });
    } else {
      const left = Math.max(8, Math.min(window.innerWidth - width - 8, r.right - width));
      const top = Math.max(8, r.top - height);
      setActiveTarget(targetEl);
      setPickerAnchor({ top, left, mode: 'fixed' });
    }
  };

  const closePicker = () => {
    setActiveTarget(null);
    setPickerAnchor(null);
    setCustom('');
  };

  const pick = (emoji) => {
    if (emoji && activeTarget) insertAtCursor(activeTarget, emoji);
    const t = activeTarget;
    closePicker();
    setTimeout(() => t?.focus({ preventScroll: true }), 0);
  };

  const pickAndRemember = (emoji) => {
    const clean = (emoji || '').trim();
    if (clean) saveEmoji(clean);
    pick(clean);
  };

  // If the active target is inside a Radix dialog, portal the picker there so
  // its paste-emoji input isn't hijacked by the dialog's focus trap.
  const pickerPortalTarget = (activeTarget && activeTarget.closest?.('[role="dialog"]')) || document.body;

  return (
    <>
      {elements.map((el, i) => (
        <PortalTo key={i} target={document.body}>
          <div data-emoji-ui="true">
            <AnchoredButton el={el} onOpen={openPicker} />
          </div>
        </PortalTo>
      ))}
      {activeTarget && pickerAnchor && createPortal(
        <div data-emoji-ui="true">
          <div
            className="fixed inset-0"
            style={{ pointerEvents: 'auto', zIndex: 9998 }}
            onMouseDown={(e) => { e.preventDefault(); closePicker(); }}
          />
          <div
            className="w-72 bg-white rounded-lg shadow-xl border border-slate-200 p-3"
            style={{ position: pickerAnchor.mode || 'fixed', top: pickerAnchor.top, left: pickerAnchor.left, pointerEvents: 'auto', zIndex: 9999 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">emoji keyboard</div>
              <button
                type="button"
                onClick={closePicker}
                className="text-[10px] text-slate-400 hover:text-pink-600"
              >close</button>
            </div>
            <div className="grid grid-cols-8 gap-1 mb-3 max-h-56 overflow-y-auto">
              {emojis.map((e) => (
                <button
                  type="button"
                  key={e}
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => pick(e)}
                  className="w-8 h-8 rounded-md text-lg hover:bg-pink-50 transition-transform hover:scale-110"
                  data-testid={`global-emoji-fav-${e}`}
                >{e}</button>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">or paste any emoji</div>
              <div className="flex items-center gap-2">
                <input
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && custom.trim()) pickAndRemember(custom.trim()); }}
                  placeholder="🌟"
                  className="h-8 flex-1 text-lg text-center border border-slate-200 rounded outline-none focus:ring-2 focus:ring-pink-200"
                  data-testid="global-emoji-custom-input"
                />
                <button
                  type="button"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => { if (custom.trim()) pickAndRemember(custom.trim()); }}
                  disabled={!custom.trim()}
                  className="text-[11px] font-semibold text-white bg-pink-500 hover:bg-pink-600 disabled:opacity-50 rounded px-2 py-1"
                >insert</button>
              </div>
            </div>
          </div>
        </div>,
        pickerPortalTarget,
      )}
    </>
  );
};

const PortalTo = ({ target, children }) => createPortal(children, target);

export default GlobalEmojiInserter;
